import { css } from 'lit';
import { Action } from 'redux';
import { MapLayerGroups } from '@/config/ConfigCARSx';
import { Geometry } from 'geojson';
import { ThunkActionRoot } from './redux-store';
import { FilterType } from '../config/config.d';

//	STATE

export interface MapState {
	bounds?: L.LatLngBoundsExpression; //	what the map shows currently
	moveTo?: L.LatLngExpression | L.LatLngBoundsExpression; //	prompts the map to move, after which this is immediately set to undefined
	activeFilters: Set<FilterType['value']>;
	lastPosition?: L.LatLngExpression;
	markerId?: number;
	mapLayerGroup?: MapLayerGroups;
	// TODO: make this an arbitrary feature collection in the future
	routeGeometry?: Geometry;
	zoom?: number;
}

export const MAP_STATE_INITIAL: MapState = {
	bounds: undefined,
	moveTo: undefined,
	activeFilters: new Set(),
	lastPosition: undefined,
	markerId: undefined,
	mapLayerGroup: undefined,
	routeGeometry: undefined,
	zoom: undefined,
};

//	ACTION TYPING

export enum MapActionType {
	MAP_FIT_TO_BOUNDS = 'MAP_FIT_TO_BOUNDS',
	MAP_MOVE_TO_POINT = 'MAP_MOVE_TO_POINT',
	MAP_CLEAR_DESTINATION = 'MAP_CLEAR_DESTINATION',
	MAP_SET_ACTIVE_FILTERS = 'MAP_SET_ACTIVE_FILTERS',
	MAP_SET_LAST_POSITION = 'MAP_SET_LAST_POSITION',
	MAP_MOVE_TO_MARKER = 'MAP_MOVE_TO_MARKER',
	MAP_SET_ROUTE_GEOMETRY = 'MAP_SET_ROUTE_GEOMETRY',
	MAP_SET_ZOOM = 'MAP_SET_ZOOM',
}

interface MapFitToBounds extends Action<typeof MapActionType.MAP_FIT_TO_BOUNDS> {
	bounds: L.LatLngBoundsExpression;
}

interface MapMoveToPoint extends Action<typeof MapActionType.MAP_MOVE_TO_POINT> {
	point: L.LatLngExpression;
}

type MapClearDestination = Action<typeof MapActionType.MAP_CLEAR_DESTINATION>;

interface MapSetActiveFilters extends Action<typeof MapActionType.MAP_SET_ACTIVE_FILTERS> {
	activeFilters: MapState['activeFilters'];
}

interface MapSetLastPosition extends Action<typeof MapActionType.MAP_SET_LAST_POSITION> {
	lastPosition: MapState['lastPosition'];
}

interface MapMoveToMarker extends Action<typeof MapActionType.MAP_MOVE_TO_MARKER> {
	markerId: number;
	mapLayerGroup: MapLayerGroups;
}

interface MapSetRouteGeometry extends Action<typeof MapActionType.MAP_SET_ROUTE_GEOMETRY> {
	geometry?: Geometry;
}

interface MapSetZoom extends Action<typeof MapActionType.MAP_SET_ZOOM> {
	zoom?: number;
}

export type MapAction =
	| MapFitToBounds
	| MapMoveToPoint
	| MapClearDestination
	| MapSetActiveFilters
	| MapSetLastPosition
	| MapMoveToMarker
	| MapSetRouteGeometry
	| MapSetZoom;

//	ACTIONS

export const mapFitToBounds = (bounds: L.LatLngBoundsExpression): MapFitToBounds => ({
	type: MapActionType.MAP_FIT_TO_BOUNDS,
	bounds,
});

export const mapMoveToPoint = (point: L.LatLngExpression): MapMoveToPoint => ({
	type: MapActionType.MAP_MOVE_TO_POINT,
	point,
});

export const mapClearDestination =
	(): ThunkActionRoot<void> =>
	(dispatch): void => {
		dispatch({
			type: MapActionType.MAP_CLEAR_DESTINATION,
		});
	};

export const mapSetActiveFilters = (
	activeFilters: MapState['activeFilters'],
): MapSetActiveFilters => ({
	type: MapActionType.MAP_SET_ACTIVE_FILTERS,
	activeFilters,
});

export const mapSetLastPosition = (lastPosition: MapState['lastPosition']): MapSetLastPosition => ({
	type: MapActionType.MAP_SET_LAST_POSITION,
	lastPosition,
});

export const mapMoveToMarker = (
	markerId: number,
	mapLayerGroup: MapLayerGroups,
): MapMoveToMarker => ({
	type: MapActionType.MAP_MOVE_TO_MARKER,
	markerId,
	mapLayerGroup,
});

export const mapSetRouteGeometry = (geometry?: Geometry): MapSetRouteGeometry => ({
	type: MapActionType.MAP_SET_ROUTE_GEOMETRY,
	geometry,
});

export const mapSetZoom = (zoom?: number): MapSetZoom => ({
	type: MapActionType.MAP_SET_ZOOM,
	zoom,
});
//	REDUCER

export const MapReducer = (
	state: MapState = MAP_STATE_INITIAL,
	action: MapAction | undefined = undefined,
): MapState => {
	if (action === undefined) {
		return state;
	}
	switch (action.type) {
		case MapActionType.MAP_FIT_TO_BOUNDS:
			return {
				...state,
				moveTo: action.bounds,
			};
		case MapActionType.MAP_MOVE_TO_POINT:
			return {
				...state,
				moveTo: action.point,
			};
		case MapActionType.MAP_CLEAR_DESTINATION:
			return {
				...state,
				moveTo: undefined,
				markerId: undefined,
				mapLayerGroup: undefined,
			};
		case MapActionType.MAP_SET_ACTIVE_FILTERS:
			return {
				...state,
				activeFilters: action.activeFilters,
			};
		case MapActionType.MAP_SET_LAST_POSITION:
			return {
				...state,
				lastPosition: action.lastPosition,
			};
		case MapActionType.MAP_MOVE_TO_MARKER:
			return {
				...state,
				markerId: action.markerId,
				mapLayerGroup: action.mapLayerGroup,
			};
		case MapActionType.MAP_SET_ROUTE_GEOMETRY:
			return {
				...state,
				routeGeometry: action.geometry,
			};
		case MapActionType.MAP_SET_ZOOM:
			return {
				...state,
				zoom: action.zoom,
			};
		default:
			return state;
	}
};
